import React from 'react';
import ImagePlaceholder from './ImagePlaceholder';
import Particles from './Particles';
import useFadeIn from './useFadeIn';

const PHONE_HREF = 'tel:+48536030706';

export default function Hero() {
  const textRef = useFadeIn();
  const imgRef = useFadeIn();

  const scrollTo = (id) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #050a1a 0%, #0a1628 40%, #0d1f3c 70%, #050a1a 100%)',
      }}
    >
      {/* Ambient glows */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
          top: '-100px',
          right: '-100px',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(26,52,96,0.3) 0%, transparent 70%)',
          bottom: '-50px',
          left: '-50px',
          pointerEvents: 'none',
        }}
      />

      <Particles count={25} />

      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '100px 24px 60px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 60,
          alignItems: 'center',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {/* Left – text */}
        <div ref={textRef} className="fade-in-section">
          <p
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.4em',
              textTransform: 'uppercase',
              color: '#3b82f6',
              marginBottom: 20,
            }}
          >
            INFINITI FX35
          </p>

          <h1
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: 'clamp(44px, 6vw, 80px)',
              fontWeight: 700,
              lineHeight: 1.1,
              color: '#f4f5f7',
              marginBottom: 24,
              letterSpacing: '-0.02em',
            }}
          >
            AUTO DO{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              ŚLUBU
            </span>
          </h1>

          <p
            style={{
              fontSize: 18,
              fontWeight: 300,
              color: 'rgba(244,245,247,0.7)',
              lineHeight: 1.6,
              marginBottom: 16,
              maxWidth: 480,
            }}
          >
            Luksusowy SUV, który podkreśli wyjątkowy charakter Waszego dnia.
          </p>

          <p
            style={{
              fontSize: 14,
              color: 'rgba(244,245,247,0.5)',
              lineHeight: 1.7,
              marginBottom: 40,
              maxWidth: 440,
            }}
          >
            Eleganckie nadwozie, komfortowe jasne wnętrze i niezapomniane wrażenia dla Pary Młodej.
          </p>

          {/* CTA buttons */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <a
              href={PHONE_HREF}
              className="btn-primary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.22 2 2 0 012 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14z" />
              </svg>
              Zarezerwuj termin
            </a>

            <button
              onClick={() => scrollTo('#gallery')}
              className="btn-outline"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              Zobacz galerię
            </button>
          </div>

          {/* Trust badges */}
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 48,
              flexWrap: 'wrap',
            }}
          >
            {['✓  Komfort', '✓  Elegancja', '✓  Wyjątkowe chwile'].map((badge) => (
              <div
                key={badge}
                className="glass-card"
                style={{
                  padding: '10px 16px',
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: '0.05em',
                  color: 'rgba(244,245,247,0.8)',
                }}
              >
                {badge}
              </div>
            ))}
          </div>
        </div>

        {/* Right – car image */}
        <div ref={imgRef} className="fade-in-section" style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              inset: -40,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
              animation: 'glowPulse 3s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />

          {/*
            PODMIEŃ ZDJĘCIE:
            Umieść plik hero-car.jpg w /public/images/
            i zamień <ImagePlaceholder> na:
            <img src="/images/hero-car.jpg" alt="Infiniti FX35 – auto do ślubu" style={{ width:'100%', borderRadius:16, animation:'float 6s ease-in-out infinite' }} />
          */}
          <div
            style={{
              borderRadius: 16,
              overflow: 'hidden',
              boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(59,130,246,0.1)',
              animation: 'float 6s ease-in-out infinite',
            }}
          >
            <ImagePlaceholder
              label="Główne zdjęcie Infiniti FX35&#10;(hero-car.jpg)"
              aspectRatio="16/10"
            />
          </div>

          {/* Decorative lines */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: -24,
              right: -24,
              width: 120,
              height: 120,
              borderRight: '1px solid rgba(59,130,246,0.2)',
              borderBottom: '1px solid rgba(59,130,246,0.2)',
              borderRadius: '0 0 8px 0',
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -24,
              left: -24,
              width: 120,
              height: 120,
              borderLeft: '1px solid rgba(59,130,246,0.2)',
              borderTop: '1px solid rgba(59,130,246,0.2)',
              borderRadius: '8px 0 0 0',
            }}
          />
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          opacity: 0.4,
          zIndex: 2,
        }}
        aria-hidden="true"
      >
        <span style={{ fontSize: 9, letterSpacing: '0.3em', color: '#f4f5f7' }}>SCROLL</span>
        <div
          style={{
            width: 1,
            height: 40,
            background: 'linear-gradient(180deg, rgba(244,245,247,0.8), transparent)',
            animation: 'float 2s ease-in-out infinite',
          }}
        />
      </div>
    </section>
  );
}
